/**
 * Drawer Provider
 * Manages cross-domain drawer navigation for the customer portal
 */

import { useState, useCallback, useMemo, createContext, useContext } from 'react'
import { ordersApi } from '@spcc/domains/orders'
import { shipmentsApi } from '@spcc/domains/shipments'
import { salesOrdersApi } from '@spcc/domains/sales-orders'
import { OrderDetailDrawer } from '@spcc/domains/orders'
import { ShipmentDetailDrawer } from '@spcc/domains/shipments'
import { SalesOrderDetailDrawer } from '@spcc/domains/sales-orders'
import type { Order } from '@spcc/domains/orders'
import type { Shipment } from '@spcc/domains/shipments'
import type { SalesOrder } from '@spcc/domains/sales-orders'

/** Drawer navigation context value */
interface DrawerContextValue {
  openOrderDrawer: (orderId: string) => Promise<void>
  openShipmentDrawer: (shipmentId: string) => Promise<void>
  openSalesOrderDrawer: (salesOrderId: string) => Promise<void>
}

const DrawerContext = createContext<DrawerContextValue | null>(null)

/** Hook to access drawer navigation */
export function useDrawerNavigation(): DrawerContextValue {
  const context = useContext(DrawerContext)
  if (!context) {
    throw new Error('useDrawerNavigation must be used within DrawerProvider')
  }
  return context
}

/** Optional version that returns null if outside provider */
export function useDrawerNavigationOptional(): DrawerContextValue | null {
  return useContext(DrawerContext)
}

interface DrawerState<T> {
  isOpen: boolean
  data: T | null
}

interface DrawerProviderProps {
  children: React.ReactNode
}

export function DrawerProvider({ children }: DrawerProviderProps) {
  // Drawer states
  const [orderDrawer, setOrderDrawer] = useState<DrawerState<Order>>({
    isOpen: false,
    data: null,
  })
  const [shipmentDrawer, setShipmentDrawer] = useState<DrawerState<Shipment>>({
    isOpen: false,
    data: null,
  })
  const [salesOrderDrawer, setSalesOrderDrawer] = useState<DrawerState<SalesOrder>>({
    isOpen: false,
    data: null,
  })

  // Open order drawer
  const openOrderDrawer = useCallback(async (orderId: string) => {
    const order = await ordersApi.getOrder(orderId)
    if (order) {
      setOrderDrawer({ isOpen: true, data: order })
    }
  }, [])

  // Open shipment drawer
  const openShipmentDrawer = useCallback(async (shipmentId: string) => {
    const shipment = await shipmentsApi.getShipment(shipmentId)
    if (shipment) {
      setShipmentDrawer({ isOpen: true, data: shipment })
    }
  }, [])

  // Open sales order drawer
  const openSalesOrderDrawer = useCallback(async (salesOrderId: string) => {
    const salesOrder = await salesOrdersApi.getSalesOrder(salesOrderId)
    if (salesOrder) {
      setSalesOrderDrawer({ isOpen: true, data: salesOrder })
    }
  }, [])

  // Close handlers
  const handleOrderClose = useCallback((open: boolean) => {
    if (!open) {
      setOrderDrawer({ isOpen: false, data: null })
    }
  }, [])

  const handleShipmentClose = useCallback((open: boolean) => {
    if (!open) {
      setShipmentDrawer({ isOpen: false, data: null })
    }
  }, [])

  const handleSalesOrderClose = useCallback((open: boolean) => {
    if (!open) {
      setSalesOrderDrawer({ isOpen: false, data: null })
    }
  }, [])

  // Context value
  const contextValue = useMemo<DrawerContextValue>(() => ({
    openOrderDrawer,
    openShipmentDrawer,
    openSalesOrderDrawer,
  }), [openOrderDrawer, openShipmentDrawer, openSalesOrderDrawer])

  return (
    <DrawerContext.Provider value={contextValue}>
      {children}

      {/* Order Drawer - with callbacks for tabbed navigation */}
      <OrderDetailDrawer
        order={orderDrawer.data}
        open={orderDrawer.isOpen}
        onOpenChange={handleOrderClose}
        onViewSalesOrder={openSalesOrderDrawer}
        onViewShipment={openShipmentDrawer}
      />

      {/* Shipment Drawer - with callbacks for tabbed navigation */}
      <ShipmentDetailDrawer
        shipment={shipmentDrawer.data}
        open={shipmentDrawer.isOpen}
        onOpenChange={handleShipmentClose}
        onViewOrder={openOrderDrawer}
        onViewSalesOrder={openSalesOrderDrawer}
      />

      {/* Sales Order Drawer - with callbacks for tabbed navigation */}
      <SalesOrderDetailDrawer
        contract={salesOrderDrawer.data}
        open={salesOrderDrawer.isOpen}
        onOpenChange={handleSalesOrderClose}
        onViewOrders={openOrderDrawer}
        onViewShipments={openShipmentDrawer}
      />
    </DrawerContext.Provider>
  )
}
